// ink-component
import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';

import { buttonGroupTokens } from '../../../../tokens/components/ButtonGroup/tokens';

import getGlobalProps from '../../../../helpers/constants/global-props';
import { StyledButton, ButtonProps } from '../../../Button/Button';

export interface ButtonGroupButtonProps extends ButtonProps {
  /** Changes the Button visuals, like background and text color. You can only modify it to Primary or Default */
  type?: 'default' | 'primary';
}

export const ButtonElement = styled(StyledButton)<ButtonGroupButtonProps>`
  z-index: 1;

  // Z-index elevation overrides for improved active visibility
  &:hover {
    z-index: 2;
  }

  &:focus,
  &:focus-visible {
    z-index: 3;
  }

  // Border Radius overrides to add segmented visibility
  &:not(:only-child) {
    border-radius: ${buttonGroupTokens.CoreBorderRadiusCenter};
    margin-right: -1px; // Add a negative margin to merge button's borders

    &:first-child {
      border-radius: ${buttonGroupTokens.CoreBorderRadiusHorizontalFirst};
    }

    &:last-child {
      border-radius: ${buttonGroupTokens.CoreBorderRadiusHorizontalLast};
    }

    ${({ theme }) =>
      theme.vertical &&
      css`
        margin-right: 0; // Reset negative margin from horizontally oriented ButtonGroup.
        margin-top: -1px; // Add a negative margin to merge button's borders when it's vertically oriented.

        // When vertically oriented, the first Toggle need to have rounded corners
        &:first-child {
          border-radius: ${buttonGroupTokens.CoreBorderRadiusVerticalFirst};
        }

        // When vertically oriented, the last Toggle or Dropdown element need to have rounded corners
        &:last-child {
          border-radius: ${buttonGroupTokens.CoreBorderRadiusVerticalLast};
        }
      `}
  }
`;

const globalProps = getGlobalProps({});

/**
 * @purpose A Trigger for ButtonGroup
 * @group ButtonGroup
 * @HTMLType HTMLButtonElement
 */
function ButtonGroupButton(props: ButtonGroupButtonProps) {
  const { className, style, type, isDisabled, ...otherProps } = props;

  return (
    <ThemeProvider
      theme={theme => ({
        ...theme,
        type,
        disabled: isDisabled,
      })}
    >
      <ButtonElement {...otherProps} {...globalProps} disabled={isDisabled} />
    </ThemeProvider>
  );
}

ButtonGroupButton.defaultProps = {
  type: 'default',
};

ButtonGroupButton.displayName = 'ButtonGroup.Button';

export default ButtonGroupButton;
